import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { userRedirectedFromAlternateDomain } from 'utils/metrics';

/**
 * A map of IDs to the domain names they represent, for use in tracking customers
 * who have been redirected from one of our alternate domain names to the primary
 * domain
 */
const DOMAIN_NAME_BY_ID = {
  ca: 'generationtux.ca',
} as const;

const isValidDomainId = (id: string): id is keyof typeof DOMAIN_NAME_BY_ID => DOMAIN_NAME_BY_ID.hasOwnProperty(id);

/**
 * A hook that looks at a page's URL and fires a Segment event to track when a
 * customer has been redirected from one of our alternate domains
 *
 * It will also remove the query param from the redirect's destination, in order
 * to prevent multiple tracking events from being sent for the same session
 */
const useDomainRedirectTracking = () => {
  const router = useRouter();

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);

    if (!router.isReady || !queryParams.has('originalSubdomain')) {
      return;
    }

    const domainId = queryParams.get('originalSubdomain')!;

    if (isValidDomainId(domainId)) {
      const domainName = DOMAIN_NAME_BY_ID[domainId];

      userRedirectedFromAlternateDomain(domainName);
    }

    queryParams.delete('originalSubdomain');

    router.replace({ query: queryParams.toString() }, undefined, { shallow: true });
  }, [router]);
};

export default useDomainRedirectTracking;
